import type { GroundingMetadata, TextSegment, SourceInfo } from '@/types';

/**
 * Transforms raw Gemini API response into an array of renderable text segments.
 * Mimics the internal logic of Google Studio's text parser.
 */
export function getGroundedTextSegments(
  text: string,
  groundingMetadata?: GroundingMetadata
): TextSegment[] {
  if (!groundingMetadata || !groundingMetadata.groundingSupports) {
    return [{ type: 'text', content: text }];
  }

  const { groundingSupports, groundingChunks } = groundingMetadata;
  const segments: TextSegment[] = [];
  let currentCursor = 0;

  // Sort supports by start index to ensure linear processing
  const sortedSupports = [...groundingSupports].sort(
    (a, b) => a.segment.startIndex - b.segment.startIndex
  );

  sortedSupports.forEach((support) => {
    const { startIndex, endIndex } = support.segment;
    const chunkIndices = support.groundingChunkIndices;
    const confidenceScores = support.confidenceScores || [];

    // 1. Push plain text BEFORE the citation
    if (currentCursor < startIndex) {
      segments.push({
        type: 'text',
        content: text.slice(currentCursor, startIndex),
      });
    }

    // 2. Resolve the source details for the tooltip
    const sources: SourceInfo[] = chunkIndices.map((idx, arrayIdx) => {
      const chunk = groundingChunks[idx];
      const confidence = confidenceScores[arrayIdx];
      const rc = chunk.retrievedContext;

      if (rc) {
        const title = rc.title || 'Document';
        const url = rc.uri || '#';
        const text = rc.ragChunk?.text || rc.text || '';
        return { title, url, text, confidence };
      }

      if (chunk.web) {
        return {
          title: chunk.web.title || 'Web Source',
          url: chunk.web.uri || '#',
          permalink: chunk.web.uri || undefined,
          confidence,
        };
      }

      return { title: 'Source', url: '#', confidence };
    });

    // 3. Push the CITED text segment
    segments.push({
      type: 'citation',
      content: text.slice(startIndex, endIndex),
      sources: sources,
    });

    currentCursor = endIndex;
  });

  // 4. Push any remaining plain text
  if (currentCursor < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(currentCursor),
    });
  }

  return segments;
}

/**
 * Extract unique sources from grounding metadata
 */
export function extractSources(groundingMetadata?: GroundingMetadata): SourceInfo[] {
  if (!groundingMetadata || !groundingMetadata.groundingSupports || !groundingMetadata.groundingChunks) {
    return [];
  }

  const { groundingSupports, groundingChunks } = groundingMetadata;
  const sourceMap = new Map<string, SourceInfo>();

  groundingSupports.forEach((support) => {
    support.groundingChunkIndices.forEach((idx) => {
      const chunk = groundingChunks[idx];
      let source: SourceInfo | null = null;

      if (chunk.retrievedContext) {
        const rc = chunk.retrievedContext;
        const title = rc.title || 'Document';
        const url = rc.uri || '#';
        const text = (rc.ragChunk?.text || rc.text || '')
          ? (rc.ragChunk?.text || rc.text || '').substring(0, 200) + '...'
          : '';
        source = { title, url, text };
      } else if (chunk.web) {
        source = {
          title: chunk.web.title || 'Web Source',
          url: chunk.web.uri || '#',
          permalink: chunk.web.uri || undefined,
        };
      }

      if (source && source.url !== '#') {
        sourceMap.set(source.url, source);
      }
    });
  });

  return Array.from(sourceMap.values());
}
